import { Star, NotePencil } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  RATING_LABELS,
  POSITIVE_TAG_LABELS,
  CONCERN_TAG_LABELS,
  type FollowUpSummary,
  type PositiveTagKey,
  type ConcernTagKey,
} from "shared/types/candidate-follow-up";

interface FollowUpCellDisplayProps {
  summary: FollowUpSummary | null;
  onClick: () => void;
  onQuickCall?: () => void;
  disabled?: boolean;
}

const FollowUpCellDisplay = ({
  summary,
  onClick,
  onQuickCall,
  disabled,
}: FollowUpCellDisplayProps) => {
  if (disabled) {
    return <span className="px-2 py-1 text-xs text-neutral-400">-</span>;
  }

  const rating = summary?.rating ?? null;
  const actionCount = summary?.actionCount ?? 0;
  const positiveTags = summary?.positiveTags ?? [];
  const concernTags = summary?.concernTags ?? [];

  const tooltipContent = (
    <div className="flex flex-col gap-1">
      <p className="font-medium">
        {rating !== null ? `${RATING_LABELS[rating]} (${rating}/5)` : "Geen score"}
      </p>
      <p className="text-xs text-neutral-400">
        {actionCount} {actionCount === 1 ? "actie" : "acties"} gelogd
      </p>

      {positiveTags.length > 0 && (
        <div className="flex flex-col gap-0.5 mt-1">
          <p className="text-xs font-medium text-green-600">Pluspunten:</p>
          {positiveTags.map((tagKey) => (
            <p key={tagKey} className="text-xs text-green-600">
              • {POSITIVE_TAG_LABELS[tagKey as PositiveTagKey] ?? tagKey}
            </p>
          ))}
        </div>
      )}

      {concernTags.length > 0 && (
        <div className="flex flex-col gap-0.5 mt-1">
          <p className="text-xs font-medium text-red-500">Aandachtspunten:</p>
          {concernTags.map((tagKey) => (
            <p key={tagKey} className="text-xs text-red-500">
              • {CONCERN_TAG_LABELS[tagKey as ConcernTagKey] ?? tagKey}
            </p>
          ))}
        </div>
      )}

      <p className="text-xs text-neutral-400 mt-1">Klik om te openen</p>
    </div>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1">
            {/* Main clickable area: stars + action count */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
              className="flex items-center gap-1.5 rounded-md px-2 py-1 transition-colors hover:bg-neutral-100"
            >
              {/* 5 Stars — always visible */}
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={14}
                    weight={rating !== null && star <= rating ? "fill" : "regular"}
                    className={
                      rating !== null && star <= rating
                        ? "text-amber-400"
                        : "text-neutral-300"
                    }
                  />
                ))}
              </div>

              {/* Action count badge — always visible */}
              <Badge
                variant="secondary"
                className="flex h-5 items-center gap-1 rounded-full px-1.5 text-xs font-medium"
              >
                {actionCount}
              </Badge>
            </button>

            {/* Open follow-up notes button */}
            {onQuickCall && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onQuickCall();
                }}
                className="flex items-center rounded-md p-1 transition-colors hover:bg-neutral-100"
                aria-label="Opvolging openen"
              >
                <NotePencil size={14} className={actionCount > 0 ? "text-moss-primary" : "text-neutral-400"} />
              </button>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default FollowUpCellDisplay;
